import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Check, AlertCircle, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_KEYS = ["dev", "it", "sec", "ai", "mkt", "iot", "doc"];
const EDUCATION_SERVICE = "Soluciones digitales para educación";

export default function ContactCTA() {
    const { dict, t, lang } = useI18n();
    const location = useLocation();
    const isEducation = new URLSearchParams(location.search).get("sector") === "educacion";
    const educationLabel = lang === "en"
        ? "Digital solutions for education"
        : EDUCATION_SERVICE;
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        service_interest: isEducation ? EDUCATION_SERVICE : "",
        message: "",
        consent: false,
    });
    const [status, setStatus] = useState("idle");

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message || !form.consent) return;
        setStatus("loading");
        try {
            await axios.post(`${API}/leads`, {
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                company: form.company || null,
                service_interest: form.service_interest || null,
                message: form.message,
                language: lang,
                source: "website",
            });
            setStatus("success");
            toast.success(t("contact.success"));
            setForm({
                name: "",
                email: "",
                phone: "",
                company: "",
                service_interest: isEducation ? EDUCATION_SERVICE : "",
                message: "",
                consent: false,
            });
        } catch (err) {
            console.error(err);
            setStatus("error");
            toast.error(t("contact.error"));
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative section-pad scroll-mt-24 overflow-hidden"
        >
            {/* background glow */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[40rem] w-[40rem] rounded-full bg-brand/15 blur-[120px]" />
                <div className="absolute inset-0 grid-bg opacity-30 [mask-image:radial-gradient(60%_50%_at_50%_50%,#000,transparent)]" />
            </div>

            <div className="container-page relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="lg:col-span-5"
                    >
                        <span className="eyebrow">{t("finalCta.eyebrow")}</span>
                        <h2
                            data-testid="contact-title"
                            className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.12]"
                        >
                            {t("finalCta.title")}
                        </h2>
                        <p className="mt-5 text-white/60 text-base md:text-lg max-w-md">
                            {t("finalCta.subtitle")}
                        </p>

                        <div className="mt-10 space-y-4 text-sm text-white/70">
                            <a
                                href="mailto:hola@nidadigital.es"
                                data-testid="contact-email-link"
                                className="flex items-center gap-3 group hover:text-white transition-colors"
                            >
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-brand-glow">
                                    <Mail className="h-4 w-4" />
                                </span>
                                hola@nidadigital.es
                            </a>
                            <div className="flex items-start gap-3">
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-brand-glow">
                                    <MapPin className="h-4 w-4" />
                                </span>
                                <span className="leading-relaxed">
                                    Carretera CM-4005, km 14.5
                                    <br />
                                    45750 Huerta de Valdecarábanos, Toledo
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7"
                    >
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="card-surface p-6 md:p-8 gradient-border rounded-2xl space-y-4"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field
                                    label={t("contact.name")}
                                    name="name"
                                    value={form.name}
                                    onChange={update("name")}
                                    required
                                />
                                <Field
                                    label={t("contact.email")}
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    required
                                />
                                <Field
                                    label={t("contact.phone")}
                                    name="phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                />
                                <Field
                                    label={t("contact.company")}
                                    name="company"
                                    value={form.company}
                                    onChange={update("company")}
                                />
                            </div>

                            <div>
                                <label className="text-xs uppercase tracking-wide text-white/65 mb-2 block">
                                    {t("contact.service")}
                                </label>
                                <select
                                    data-testid="contact-service-select"
                                    value={form.service_interest}
                                    onChange={update("service_interest")}
                                    className="w-full min-h-12 rounded-lg border border-white/10 bg-white/[0.03] px-4 pr-10 py-3 text-sm text-white outline-none focus:border-brand transition"
                                >
                                <option value="" className="bg-neutral-900 text-white">
                                    {t("contact.servicePlaceholder")}
                                </option>
                                <option value={EDUCATION_SERVICE} className="bg-neutral-900 text-white">
                                    {educationLabel}
                                </option>
                                {SERVICE_KEYS.map((k) => (
                                    <option
                                        key={k}
                                        value={dict.services.cats.find((c) => c.key === k)?.title || k}
                                        className="bg-neutral-900 text-white"
                                    >
                                    {dict.services.cats.find((c) => c.key === k)?.title}
                                    </option>
                                ))}
                                </select>
                            </div>

                            <div>
                                <label className="text-xs uppercase tracking-wide text-white/65 mb-2 block">
                                    {t("contact.message")}
                                </label>
                                <textarea
                                    data-testid="contact-message"
                                    value={form.message}
                                    onChange={update("message")}
                                    rows={4}
                                    required
                                    minLength={5}
                                    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none focus:border-brand transition resize-none"
                                />
                            </div>

                            <label className="flex items-start gap-3 pt-2 text-sm leading-relaxed text-white/65">
                                <input
                                    data-testid="contact-consent"
                                    type="checkbox"
                                    checked={form.consent}
                                    onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                                    className="mt-1 h-4 w-4 shrink-0 rounded border-white/20 bg-white/5 accent-brand"
                                    required
                                />
                                <span>
                                    {t("contact.consent")}{" "}
                                    <a
                                        href="/privacidad"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline underline-offset-4 hover:text-white"
                                    >
                                        {lang === "en" ? "View policy ↗" : "Ver política ↗"}
                                    </a>
                                </span>
                            </label>

                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={status === "loading"}
                                className="btn-primary w-full sm:w-auto group disabled:opacity-60"
                            >
                                {status === "loading" && (
                                    <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                )}
                                {status === "success" && <Check className="h-4 w-4" />}
                                {status === "error" && <AlertCircle className="h-4 w-4" />}
                                {status === "idle" && <Send className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />}
                                {status === "loading" ? t("contact.sending") : t("contact.send")}
                            </button>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Field({ label, name, value, onChange, type = "text", required = false }) {
    return (
        <div>
            <label htmlFor={name} className="text-xs uppercase tracking-wide text-white/65 mb-2 block">
                {label}
                {required && <span className="text-brand-glow ml-1">*</span>}
            </label>
            <input
                id={name}
                name={name}
                data-testid={`contact-${name}`}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                className="w-full min-h-12 rounded-lg border border-white/10 bg-white/[0.03] px-4 pr-10 py-3 text-sm text-white outline-none focus:border-brand transition"
            />
        </div>
    );
}
